import { ArrayField, BooleanField, ChipField, Datagrid, DateField, Filter, FunctionField, List, NumberField, ReferenceArrayField, ReferenceArrayInput, ReferenceField, RichTextField, SearchInput, SelectArrayInput, SelectField, Show, SimpleShowLayout, SingleFieldList, Tab, TabbedShowLayout, TextField, TopToolbar, useDataProvider, useGetMany, useGetOne, useListContext, useNotify, usePermissions, useRecordContext, useRefresh } from 'react-admin';
import Permission from '../../components/Permission';
import { PERMISSIONS, alterPriceTypes, alterPriceValueTypes, daysOfTheWeek, frequencyChoices } from '../../constants';

import { Box, Button, Chip, Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

const containerFuelDiscountSx = {
  padding: 0,
};

const richTextSx = {
  '& img': {
    maxWidth: 300,
    maxHeight: 300,
  }
};

const filterTablePrintSx = {
  display: 'none',
  '@media print': {
    display: 'block'
  },
  marginBottom: '10px',
};

const titleCardSx = {
  textAlign: 'center',
  fontWeight: 'bold',
};

// Estilo fora do JSX condicional: o plugin de i18n embrulha literal de texto dentro de `&&`/ternário (ver v2/ui/tokens.ts).
const weeklyTableSx = { border: '1px solid #e0e0e0' };
// As abas rolam de lado no celular. O layout do painel é `min-width: fit-content`: sem `width: 0; min-width: 100%` a largura
// total das abas (618 px) alarga a tela inteira em vez de rolar só a fileira de abas.
// Tabelas largas rolam de lado dentro da caixa, sem alargar a tela.
const scrollBoxSx = { width: 0, minWidth: '100%', overflowX: 'auto' };
const tabsSx = { '& .MuiTabs-root': { width: 0, minWidth: '100%' } };
const actionsRowSx = { mb: 3 };

// Datas e horários do desconto são de Brasília (como na lista e no cadastro), em qualquer fuso do navegador.
const brDateTime = (value?: string | Date | null) => (value ? `${dayMonthYear(value)} ${clockStr(value)}` : '-');
import { uniq, uniqBy } from 'lodash';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { FillinsDatagrids, PrintButton, ReportPrint } from '../../components';
import { PartiallyHiddenCpfField } from '../../components/Fields';
import { getChainId, getUserId } from '../../lib';
import { DispatchMessageType, FuelDiscountCostValueType } from '../../models';
import { DispatchMessageStatus } from '../../models/dispatchMessageStatus';
import CloneDiscountButton, { showActionSx } from './CloneDiscountButton';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import { StatusChip, TypeBadge } from './list/DiscountChips';
import DisableDiscountDialog from './list/DisableDiscountDialog';
import { DiscountStatusKey, DiscountTypeKey, clockStr, dayMonthYear, getDiscountStatus, typeKey } from './list/discountStatus';
import { cardTitle } from './list/discountSummary';
import { useDiscountLookups } from './list/useDiscountLookups';
import { DiscountFrequency } from '../../models/discount';
import { __t } from '../../i18n/t';

const SingleFieldChips = (props) => {
    const { data } = useListContext();
    return (
        !data || !data.length ?
            <FunctionField render={() => props.emptyText || 'Sem informações'} />
            :
            <SingleFieldList>
                <ChipField source={props.source} />
            </SingleFieldList>
    );
};

const PriceValueField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        record.alterPriceValueType === 'percentage' ?
            <FunctionField
                render={data => data && data.alterPriceValue ? `${data.alterPriceValue}%` : '0%'}
            />
            :
            <NumberField
                source={props.source}
                emptyText="R$ 0,00"
                options={{
                    style: 'currency',
                    currency: 'BRL',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }}
            />
    );
}

const AmountField = (props) => {
    const record = useRecordContext();
    if (!record || record.uniqueDiscountValue) { return null; }

    return (
        <NumberField
            {...props}
            label="Volume"
            textAlign="center"
            sortable={false}
            source="amount"
            emptyText="0"
            options={{
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }}
        />
    )
}

const FuelDiscountsList = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout
            sx={containerFuelDiscountSx}
            record={{ ...record, fuelDiscounts: uniqBy(record.fuelDiscounts, (v: any) => [v.fuelId, v.alterPriceValue, v.alterPriceType, v.alterPriceValueType, v.costValueType, v.costValue].join()) }}
        >
            <ArrayField source="fuelDiscounts" label="Descontos por combustíveis">
                <Datagrid style={{ padding: 0 }} bulkActionButtons={false}>
                    <ReferenceField source="fuelId" reference="fuels" label="Combustível" link={false} sortable={false}>
                        <TextField source="name" />
                    </ReferenceField>
                    {
                        props.chain && props.chain.isPriceIncreaseEnabled &&
                        <FunctionField
                            render={data => data && data.alterPriceType ? alterPriceTypes[data.alterPriceType] : 'Desconto'}
                            label="Tipo"
                        />
                    }
                    {
                        props.chain && props.chain.isPriceIncreaseEnabled &&
                        <FunctionField
                            render={data => data && data.alterPriceValueType ? alterPriceValueTypes[data.alterPriceValueType] : 'Centavos'}
                            label="Tipo de valor"
                            textAlign="center"
                        />
                    }
                    <PriceValueField source="alterPriceValue" label="Valor" textAlign="center" />
                    {/* <AmountField label="Volume" /> */}
                    <SelectField
                        source="costValueType"
                        label="Forma de troca"
                        choices={FuelDiscountCostValueType.fuelDiscountCostValueTypeChoices}
                        textAlign="center"
                    />
                    <FunctionField
                        label="Custo"
                        textAlign="center"
                        sortable={false}
                        render={record => (
                            <NumberField
                                source="costValue"
                                textAlign="center"
                                emptyText={record?.costValueType === FuelDiscountCostValueType.dotz ? 'R$0,00' : '0'}
                                options={{
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    ...(record?.costValueType === FuelDiscountCostValueType.dotz ? {
                                        style: 'currency',
                                        currency: 'BRL',
                                    } : {})
                                }}
                            />
                        )}
                    />
                    <NumberField
                        source="daysToExpire"
                        label="Validade em dias"
                        emptyText="--"
                        textAlign="center"
                    />
                    {/* <BooleanField source="uniqueDiscountValue" label="Valor do desconto único" /> */}
                </Datagrid>
            </ArrayField>
        </SimpleShowLayout>
    );
}

const StationListField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerFuelDiscountSx} record={{ ...record, stationIds: uniq(record.placeDiscounts?.map(fd => fd.placeId)) }}>
            <ReferenceArrayField label="Postos" reference="stations" source="stationIds">
                <SingleFieldChips source="name" emptyText="Sem postos" />
            </ReferenceArrayField>
        </SimpleShowLayout>
    );
}
const GroupListField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerFuelDiscountSx} record={{ ...record, customerGroupIds: uniq(record.groupDiscounts?.map(fd => fd.groupId)) }}>
            <ReferenceArrayField label="Grupos" reference="chains/chainId/customer-groups" source="customerGroupIds">
                <SingleFieldChips source="name" emptyText="Sem Grupos" />
            </ReferenceArrayField>
        </SimpleShowLayout>
    );
}

const CustomerTagListField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerFuelDiscountSx} record={{ ...record }}>
            <ReferenceArrayField label="Tags" reference="chains/chainId/tag" source="customerTagIds">
                <SingleFieldChips source="name" emptyText="Sem Tags" />
            </ReferenceArrayField>
        </SimpleShowLayout>
    );
}

const CitiesFieldList = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerFuelDiscountSx}>
            <ReferenceArrayField label="Cidades" reference="cities" source="cityIds">
                <SingleFieldList linkType={false} empty={<FunctionField render={() => 'Sem cidades'} />}>
                    <ChipField source="name" />
                </SingleFieldList>
            </ReferenceArrayField>
        </SimpleShowLayout>
    );
}

// Box components for redesigned layout
const BasicInfoBox = () => {
    const record = useRecordContext();
    const { permissions } = usePermissions();

    if (!record) return null;

    return (
        <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <InfoIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Informações Básicas</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Código</Typography>
                        <Typography variant="body1" fontWeight={600}>{record.identify || '-'}</Typography>
                    </Grid>
                    {record.description && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Descrição</Typography>
                            <Typography variant="body1">{record.description}</Typography>
                        </Grid>
                    )}
                    {record.createdByEmployeeId && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Criado por</Typography>
                            <ReferenceField source="createdByEmployeeId" reference="employees" link={permissions && permissions.includes(PERMISSIONS.UPDATE_USER)}>
                                <FunctionField render={rec => rec ? (
                                    <Typography variant="body1" fontWeight={600}>
                                        {rec.name} {rec.id === getUserId() ? '(você)' : ''}
                                    </Typography>
                                ) : null} />
                            </ReferenceField>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
};

const SegmentationBox = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [cities, setCities] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [stations, setStations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!record) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch cities
                if (record.cityIds?.length > 0) {
                    const { data } = await dataProvider.getMany('cities', { ids: record.cityIds });
                    setCities(data || []);
                }
                // Fetch groups
                const groupIds = uniq(record.groupDiscounts?.map((gd: any) => gd.groupId) || []);
                if (groupIds.length > 0) {
                    const { data } = await dataProvider.getMany('chains/chainId/customer-groups', { ids: groupIds });
                    setGroups(data || []);
                }
                // Fetch tags
                if (record.customerTagIds?.length > 0) {
                    const { data } = await dataProvider.getMany('chains/chainId/tag', { ids: record.customerTagIds });
                    setTags(data || []);
                }
                // Fetch stations
                const stationIds = uniq(record.placeDiscounts?.map((pd: any) => pd.placeId) || []);
                if (stationIds.length > 0) {
                    const { data } = await dataProvider.getMany('stations', { ids: stationIds });
                    setStations(data || []);
                }
            } catch (error) {
                console.error('Error fetching segmentation data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [record, dataProvider]);

    if (!record) return null;

    const hasData = cities.length > 0 || groups.length > 0 || tags.length > 0 || stations.length > 0;

    return (
        <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <GroupIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Segmentação</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                {loading ? (
                    <Typography variant="body2" color="text.secondary">Carregando...</Typography>
                ) : !hasData ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma segmentação configurada (disponível para todos)
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {cities.length > 0 && (
                            <Grid size={12}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Cidades
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {cities.map((city: any) => (
                                        <Chip key={city.id} label={city.name} size="small" sx={{ mb: 0.5 }} />
                                    ))}
                                </Stack>
                            </Grid>
                        )}
                        {groups.length > 0 && (
                            <Grid size={12}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Grupos
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {groups.map((group: any) => (
                                        <Chip key={group.id} label={group.name} size="small" color="primary" sx={{ mb: 0.5 }} />
                                    ))}
                                </Stack>
                            </Grid>
                        )}
                        {tags.length > 0 && (
                            <Grid size={12}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Tags
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {tags.map((tag: any) => (
                                        <Chip key={tag.id} label={tag.name} size="small" color="secondary" sx={{ mb: 0.5 }} />
                                    ))}
                                </Stack>
                            </Grid>
                        )}
                        {stations.length > 0 && (
                            <Grid size={12}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Postos ({stations.length})
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {stations.slice(0, 5).map((station: any) => (
                                        <Chip key={station.id} label={station.name} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                                    ))}
                                    {stations.length > 5 && (
                                        <Chip label={`+${stations.length - 5}`} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                                    )}
                                </Stack>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Paper>
        </Grid>
    );
};

const LimitsBox = () => {
    const record = useRecordContext();

    if (!record) return null;

    return (
        <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <BlockIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Limites de Uso</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Limite de uso</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {record.usageLimit ? `${record.usageLimit} uso${record.usageLimit > 1 ? 's' : ''}` : 'Ilimitado'}
                        </Typography>
                    </Grid>
                    {record.intervalInHours && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Intervalo entre usos</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {record.intervalInHours} hora{record.intervalInHours > 1 ? 's' : ''}
                            </Typography>
                        </Grid>
                    )}
                    {record.disabledAt && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Desativado em</Typography>
                            <Typography variant="body1" fontWeight={600} color="error">
                                {brDateTime(record.disabledAt)}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
};

const ScheduleBox = () => {
    const record = useRecordContext();

    if (!record || (!record.startAt && !record.endAt && !record.frequency)) return null;

    return (
        <Grid size={{ xs: 12, md: record.frequency === DiscountFrequency.Weekly ? 6 : 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Agendamento</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">Data inicial</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {brDateTime(record.startAt)}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">Data final</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {brDateTime(record.endAt)}
                        </Typography>
                    </Grid>
                    {record.frequency && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Frequência</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {frequencyChoices.find(f => f.id === record.frequency)?.name || '-'}
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                {record.frequency === DiscountFrequency.Weekly && record.dayScheduledDiscounts && (
                    <Box mt={2}>
                        <Typography variant="subtitle2" mb={1}>Horários Semanais</Typography>
                        <TableContainer component={Paper} elevation={0} sx={weeklyTableSx}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell><strong>Dia</strong></TableCell>
                                        <TableCell align="center"><strong>Horário</strong></TableCell>
                                        <TableCell align="center"><strong>Status</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {daysOfTheWeek.map((day) => {
                                        const schedule = record.dayScheduledDiscounts?.find((s: any) => s.dayOfTheWeek === day.id);
                                        const isActive = schedule && schedule.startTime && schedule.endTime;

                                        return (
                                            <TableRow key={day.id}>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                    {day.name}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                                                    {isActive
                                                        ? `${clockStr(schedule.startTime)} - ${clockStr(schedule.endTime)}`
                                                        : '-'
                                                    }
                                                </TableCell>
                                                <TableCell align="center">
                                                    {isActive ? (
                                                        <Chip label="Ativo" size="small" color="success" />
                                                    ) : (
                                                        <Chip label="Inativo" size="small" color="default" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {record.frequency === DiscountFrequency.Daily && record.dayScheduledDiscounts && record.dayScheduledDiscounts[0] && (
                    <Grid container spacing={2} mt={1}>
                        <Grid size={6}>
                            <Typography variant="caption" color="text.secondary">Hora de início</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {record.dayScheduledDiscounts[0].startTime
                                    ? clockStr(record.dayScheduledDiscounts[0].startTime)
                                    : '-'
                                }
                            </Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography variant="caption" color="text.secondary">Hora de fim</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {record.dayScheduledDiscounts[0].endTime
                                    ? clockStr(record.dayScheduledDiscounts[0].endTime)
                                    : '-'
                                }
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </Grid>
    );
};

// O campo de CPF mascarado não declara as props do react-admin (source, label): aceita qualquer uma.
const CpfField = PartiallyHiddenCpfField as React.ComponentType<any>;

const eligibleBoxSx = { p: 3, textAlign: 'center' };
const eligibleEmptySx = { p: 3, textAlign: 'center' };
const eligibleTextSx = { maxWidth: 640, mx: 'auto' };

const EligibleNote = ({ title, children }: { title: string; children?: React.ReactNode }) => (
    <Box sx={eligibleBoxSx}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="body1" color="text.secondary" sx={eligibleTextSx}>{children}</Typography>
    </Box>
);

// Nomes de etiquetas, grupos ou cidades pelos ids do desconto.
const useNames = (resource: string, ids: string[]): string[] => {
    const { data } = useGetMany(resource, { ids }, { enabled: ids.length > 0 });
    return (data || []).map((item: any) => item?.name).filter(Boolean);
};

// Promoção só por cidade (ou etiqueta/grupo mais cidade): o cadastro de cada cliente é que diz se entra; não dá para listar.
const EligibleByCity = ({ tagIds, groupIds, cityIds }: { tagIds: string[]; groupIds: string[]; cityIds: string[] }) => {
    const cities = useNames('cities', cityIds);
    const tags = useNames('chains/chainId/tag', tagIds);
    const groups = useNames('chains/chainId/customer-groups', groupIds);
    const who = [...tags, ...groups];
    return (
        <EligibleNote title="Clientes de certas cidades">
            {`Este desconto vale para quem mora em: ${cities.join(', ') || `${cityIds.length} cidade(s)`}.`}
            {who.length > 0 ? ` E que tem uma destas etiquetas ou grupos: ${who.join(', ')}.` : ''}
            {' Quem não tem cidade no cadastro não recebe. A lista de clientes não aparece aqui porque depende da cidade do cadastro de cada um.'}
        </EligibleNote>
    );
};

const customerColumns = (
    <Datagrid bulkActionButtons={false}>
        <TextField source="name" label="Nome" />
        <CpfField source="cpf" label="CPF" />
    </Datagrid>
);

// Lista fixa de clientes (Painel 360, relatório ou cupom de campanha): vem de customer-discounts.
const CustomerDiscountList = ({ record, recovery }: { record: any; recovery: boolean }) => (
    <Box>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2 }}>
            {recovery
                ? 'Clientes que receberam o cupom da campanha de recuperação.'
                : 'Lista fixa de clientes (Painel 360 ou relatório). Só eles recebem este desconto: quem entrar no segmento depois não recebe.'}
        </Typography>
        <List
            resource="customer-discounts"
            title=" - Clientes"
            filter={{ discountId: record.id }}
            filters={[<SearchInput source="search" alwaysOn key="search" />]}
            exporter={false}
            disableSyncWithLocation
            empty={
                <Box sx={eligibleEmptySx}>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum cliente nesta lista. Se o desconto foi criado sem público definido, ele vale para todos os clientes do app.
                    </Typography>
                </Box>
            }
        >
            {customerColumns}
        </List>
    </Box>
);

const CustomerList = (props) => {
    const record = useRecordContext<any>();
    if (!record) { return null; }

    const kind = typeKey(record);
    const tagIds: string[] = record.customerTagIds || [];
    const groupIds: string[] = record.customerGroupIds?.length ? record.customerGroupIds : uniq((record.groupDiscounts || []).map((g: any) => g.groupId).filter(Boolean)) as string[];
    const cityIds: string[] = record.cityIds || [];
    const targeted = tagIds.length + groupIds.length + cityIds.length > 0;
    // `hasCustomers` vem do servidor. Sem ele (servidor antigo), promoção sem data e sem etiqueta, grupo ou cidade tem lista de clientes.
    const hasList = record.hasCustomers === true
        || (typeof record.hasCustomers !== 'boolean' && ((kind === DiscountTypeKey.special && !record.startAt && !record.endAt && !targeted) || kind === DiscountTypeKey.recovery));

    if (kind === DiscountTypeKey.reward) {
        return (
            <EligibleNote title="Quem conquista o brinde">
                Esta Recompensa vale para quem conquista o brinde do Clube de Vantagens que a usa. Não há lista de clientes aqui.
            </EligibleNote>
        );
    }

    if (hasList) {
        return <CustomerDiscountList record={record} recovery={kind === DiscountTypeKey.recovery} />;
    }

    if (kind === DiscountTypeKey.recovery) {
        return (
            <EligibleNote title="Quem recebe o cupom da campanha">
                Só quem recebe o cupom da campanha de recuperação vê este desconto.
            </EligibleNote>
        );
    }

    if (cityIds.length > 0) {
        return <EligibleByCity tagIds={tagIds} groupIds={groupIds} cityIds={cityIds} />;
    }

    if (!targeted) {
        return (
            <EligibleNote title="Desconto disponível para todos os clientes">
                Este desconto não tem etiqueta, grupo, cidade nem lista de clientes. Vale para qualquer cliente que abasteça pelo app nos postos escolhidos.
            </EligibleNote>
        );
    }

    const listFilters = [
        <SearchInput source="search" alwaysOn />
    ];

    const CustomerDatagrid = () => (
        <Datagrid
            bulkActionButtons={false}
            empty={
                <Box sx={eligibleEmptySx}>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum cliente encontrado com a segmentação definida para este desconto
                    </Typography>
                </Box>
            }
        >
            <TextField source="name" label="Nome" />
            <CpfField source="cpf" label="CPF" />
        </Datagrid>
    );

    return (
        <List
            resource="customers"
            title=" - Clientes"
            filter={{ orFilters: {
                groupIds,
                tagIds,
            }}}
            filters={listFilters}
            exporter={false}
            disableSyncWithLocation
            empty={
                <Box sx={eligibleEmptySx}>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum cliente encontrado com a segmentação definida para este desconto
                    </Typography>
                </Box>
            }
        >
            <CustomerDatagrid />
        </List>
    );
}

const NotificationList = (props) => {
    const Panel = () => (
        <ArrayField source="dispatchMessages" label="">
            <Datagrid bulkActionButtons={false}>
                <FunctionField render={record => record && DispatchMessageType.translateType(record.type)} label="Tipo" />
                <NumberField source="cardinality" label="Total" textAlign="center" />
                <FunctionField render={record => record && DispatchMessageStatus.translateType(record.status)} label="Status" textAlign="center" />
            </Datagrid>
        </ArrayField>
    )

    const NotificationDatagrid = () => (
        <Datagrid
            expand={<Panel />}
            empty={<Typography textAlign="center">Sem notificações</Typography>}
        >
            <TextField source="title" label="Título" />
            <RichTextField source="body" label="Mensagem" sx={richTextSx} />
            <TextField source="pushMessage" label="Mensagem Push" />
            {/* <TextField source="smsMessage" label="Messagem sms" /> */}
            <DateField source="sendAt" label="Envio às" showTime locales="pt-BR" />
            <FunctionField render={record => record && record.status} />
        </Datagrid>
    );

    return (
        <ArrayField source="discountMarketingCampaigns" label="Notificações" emptyText="Sem notificações">
            <NotificationDatagrid />
        </ArrayField>
    );
}

const FillinList = () => {
    const record = useRecordContext();
    const fillinsRef = useRef<any>(null);

    if (!record) { return null; }

    const FilterFillin = (props) => (
        <Filter>
            <BetweenDatesInput alwaysOn />
            <ReferenceArrayInput
                source="fuelIds"
                reference="fuels"
                sort={{ field: "name", order: "ASC" }}
                perPage={null}
                alwaysOn
            >
                <SelectArrayInput
                    style={{ minWidth: 180 }}
                    label="Combustíveis"
                    optionText={"name"}
                />
            </ReferenceArrayInput>
        </Filter>
    );

    const ListFillinsActions = () => (
        <TopToolbar>
            <PrintButton
                printRef={fillinsRef}
            />
        </TopToolbar>
    );

    const FilterTablePrint = ({ identify }) => {
        const { filterValues } = useListContext();

        return (
            <Datagrid bulkActionButtons={false} data={[{ ...filterValues, identify }]} total={1}>
                <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
                <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
                <ReferenceArrayField label="Combustíveis" source="fuelIds" textAlign="center" reference="fuels">
                    <SingleFieldList linkType={false}>
                        <ChipField source="name" />
                    </SingleFieldList>
                </ReferenceArrayField>
                <TextField label="Código do desconto" source="identify" textAlign="center" />
            </Datagrid>
        );
    };

    return (
        <List
            title=" "
            resource="fillins"
            sort={{ field: "createdAt", order: "DESC" }}
            filterDefaultValues={{
                from: moment().startOf("day").subtract(1, "month").toISOString(),
                to: moment().endOf("day").toISOString(),
            }}
            perPage={25}
            actions={<ListFillinsActions />}
            filter={{ discountId: record.id, showSummary: true, skipJoins: true }}
            filters={<FilterFillin />}
            pagination={<ListPagination />}
            exporter={false}
            disableSyncWithLocation
        >
            <ReportPrint ref={fillinsRef} title="Abastecimentos" landscape>
                <Box sx={filterTablePrintSx}>
                    <Typography sx={titleCardSx}>Filtros</Typography>
                    <FilterTablePrint identify={record && record.identify} />
                </Box>
                <FillinsDatagrids />
            </ReportPrint>
        </List>
    )
}

// "Desativar desconto" com a mesma confirmação da lista: o texto muda com o tipo e a situação, avisa de notificação
// agendada e mostra o motivo quando o backend recusa (409 "Desconto em uso na campanha X").
const DisableAction = () => {
    const record = useRecordContext<any>();
    const refresh = useRefresh();
    const notify = useNotify();
    const lookups = useDiscountLookups(record ? [record] : [], getChainId());
    const [open, setOpen] = useState(false);
    const opener = useRef<HTMLElement | null>(null);
    if (!record) { return null; }

    const status = getDiscountStatus(record);
    // Quem já acabou não tem o que desativar (como na lista).
    if (status.key === DiscountStatusKey.ended) { return null; }
    const canDisable = status.key === DiscountStatusKey.active || status.key === DiscountStatusKey.scheduled;

    return (
        <>
            <Button
                variant="outlined"
                color="error"
                startIcon={<BlockOutlinedIcon />}
                disabled={!canDisable}
                onClick={(event: React.MouseEvent<HTMLElement>) => { opener.current = event.currentTarget; setOpen(true); }}
                sx={showActionSx}
            >
                {canDisable ? 'Desativar desconto' : 'Desconto desativado'}
            </Button>
            <DisableDiscountDialog
                record={open ? record : null}
                name={cardTitle(record, lookups.stations)}
                onClose={() => setOpen(false)}
                onDisabled={message => { setOpen(false); notify(message, { type: 'success' }); refresh(); }}
                onExited={() => { if (opener.current && document.contains(opener.current)) opener.current.focus(); }}
            />
        </>
    );
};

// Tipo, código e situação logo no topo: a mesma leitura do cartão da lista.
const DiscountShowHeader = () => {
    const record = useRecordContext<any>();
    if (!record) { return null; }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
            <TypeBadge record={record} />
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>{record.identify}</Typography>
            <StatusChip status={getDiscountStatus(record)} />
        </Box>
    );
};

const DiscountShow = (props) => {

    const [chainId] = useState(getChainId());
    if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

    const { data: chain } = useGetOne('chains', { id: chainId });
    const { permissions } = usePermissions();

    return (
        <Permission permission={PERMISSIONS.FUEL_DISCOUNTS}>
            <Show title='Desconto' {...props}>
                <DiscountShowHeader />
                <TabbedShowLayout sx={tabsSx}>
                    <Tab label="Resumo">
                        <Box sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2} sx={actionsRowSx} flexWrap="wrap" useFlexGap>
                                <DisableAction />
                                <CloneDiscountButton variant="outlined" />
                            </Stack>

                            <Grid container spacing={2}>
                                <BasicInfoBox />
                                <SegmentationBox />
                                <LimitsBox />
                                <ScheduleBox />
                            </Grid>
                        </Box>
                    </Tab>
                    <Tab label="Descontos">
                        <Box sx={scrollBoxSx}>
                            <BooleanField source="overlapsStandardDiscount" label="Sobrepor o desconto padrão" />
                            <BooleanField source="pointsWhenDiscounting" label="Pontua quando usado" />
                            <FuelDiscountsList chain={chain} />
                        </Box>
                    </Tab>
                    <Tab label="Clientes Elegíveis">
                        <CustomerList />
                    </Tab>
                    <Tab label="Notificações">
                        <Box sx={{ paddingTop: 2, paddingBottom: 5 }}>
                            <NotificationList />
                        </Box>
                    </Tab>
                    <Tab label="Abastecimentos">
                        <FillinList />
                    </Tab>
                </TabbedShowLayout>
            </Show>
        </Permission>
    )
};

export default DiscountShow;
